import React, { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Icon } from './Icon';
import styles from './Alert.module.css';

type AlertType = 'info' | 'success' | 'warning' | 'error';

// Omit event handlers that conflict with Framer Motion's motion.div prop types
type DivMotionSafeProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'>;

const alert = cva(styles.base, {
  variants: {
    type: {
      info: styles.info,
      success: styles.success,
      warning: styles.warning,
      error: styles.error,
    },
    banner: { true: styles.banner },
  },
  defaultVariants: { type: 'info' },
});

interface AlertProps extends VariantProps<typeof alert>, DivMotionSafeProps {
  /** Alert type — determines color and default icon */
  type?: AlertType;
  /** Primary message */
  message: React.ReactNode;
  /** Optional description below message */
  description?: React.ReactNode;
  /** Show type icon */
  showIcon?: boolean;
  /** Show close button */
  closable?: boolean;
  /** Called when closed */
  onClose?: () => void;
  /** Full-width banner mode (no border-radius, no border) */
  banner?: boolean;
  /** Action slot (rendered after message) */
  action?: React.ReactNode;
}

const iconColorClass: Record<AlertType, string> = {
  info: styles.iconInfo,
  success: styles.iconSuccess,
  warning: styles.iconWarning,
  error: styles.iconError,
};

const iconMap: Record<AlertType, React.ReactNode> = {
  info: <Icon name="info" size={18} />,
  success: <Icon name="check_circle" size={18} />,
  warning: <Icon name="warning" size={18} />,
  error: <Icon name="cancel" size={18} />,
};

/**
 * Alert — message banner for feedback.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | type | 'info' \| 'success' \| 'warning' \| 'error' | 'info' | Color + icon |
 * | message | ReactNode | — | Primary text |
 * | description | ReactNode | — | Secondary text |
 * | showIcon | boolean | false | Show type icon |
 * | closable | boolean | false | Show X button |
 * | onClose | () => void | — | Close callback |
 * | banner | boolean | false | Full-width banner |
 * | action | ReactNode | — | Action slot |
 *
 * @example
 * <Alert type="success" message="Saved!" showIcon />
 * <Alert type="error" message="Failed" description="Check your connection." showIcon closable />
 */
export function Alert({
  type = 'info',
  message,
  description,
  showIcon = false,
  closable = false,
  onClose,
  banner = false,
  action,
  style,
  className,
  ...props
}: AlertProps) {
  const safeProps = props;
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="alert"
          initial={{ scaleY: 1, opacity: 1 }}
          exit={{ scaleY: 0, opacity: 0 }}
          transition={{ duration: duration.slow / 1000 }}
          className={clsx(
            alert({ type, banner: banner || undefined, className }),
            description && styles.hasDescription,
          )}
          style={style}
          {...safeProps}
        >
          {showIcon && (
            <span className={clsx(styles.icon, iconColorClass[type], description && styles.iconTop)}>
              {iconMap[type]}
            </span>
          )}

          <div className={styles.content}>
            <div className={clsx(styles.message, description && styles.messageWithDescription)}>
              {message}
            </div>
            {description && (
              <div className={styles.description}>
                {description}
              </div>
            )}
          </div>

          {action && <div className={styles.action}>{action}</div>}

          {closable && (
            <button
              onClick={handleClose}
              aria-label="Close alert"
              className={clsx(styles.closeBtn, description && styles.closeBtnTop)}
            >
              <Icon name="close" size={14} />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
